import { Action } from '@wix/component-protocol/schema';
import {
  EditActionDefinition,
  NativeActionNames,
  NativeEditActionFactory,
} from '@wix/editor-platform-host-integration-apis';
import { createCustomActionExecute } from '../customActions/actionHandlers';
import { EditActionsStore } from '../editActionsStore';
import { EditAction, IEditActionBuilder } from '../types';
import { WithStylePresets } from '../stylePresets/withStylePresetsDecorator';
import { getActionId } from '../utils/getActionId';

export class NativeActionBuilder extends IEditActionBuilder<NativeActionNames> {
  build(
    actionName: NativeActionNames,
    actionDefinition: Action,
  ): EditActionDefinition<NativeActionNames> | null {
    const contributedEditAction = this.getContributedEditAction(
      actionName,
      actionDefinition,
    );

    if (!contributedEditAction) {
      return null;
    }

    const { execution } = actionDefinition;

    if (!execution) {
      return contributedEditAction;
    }

    const isOverrideAllowed =
      contributedEditAction.canBeOverriddenBy?.(execution) || false;

    if (!isOverrideAllowed) {
      return contributedEditAction;
    }

    if (!actionDefinition.displayName) {
      actionDefinition.displayName = contributedEditAction.displayName;
    }

    const actionId =
      contributedEditAction.actionId || getActionId('native', actionName);

    return {
      ...contributedEditAction,
      execute: createCustomActionExecute(
        actionName,
        actionDefinition as Required<Action>,
        actionId,
        this.ctx.createExtendedPlatformContext(actionName, actionDefinition),
      ),
    };
  }

  @WithStylePresets()
  private buildContributedEditAction(
    actionName: NativeActionNames,
    actionDefinition: Action,
    factory: EditAction,
  ): EditActionDefinition<NativeActionNames> {
    return (factory as NativeEditActionFactory)(
      this.ctx.createNativeActionContext(actionName, actionDefinition),
    );
  }

  private applyUIOverrides(
    actionName: NativeActionNames,
    resolvedAction: EditActionDefinition<NativeActionNames>,
  ): EditActionDefinition<NativeActionNames> {
    const uiOverride = EditActionsStore.getActionUIOverride(actionName) || {};

    if (Object.keys(uiOverride).length === 0) {
      return resolvedAction;
    }

    return {
      ...resolvedAction,
      ...uiOverride,
    };
  }

  private getContributedEditAction(
    actionName: NativeActionNames,
    actionDefinition: Action,
  ): EditActionDefinition<NativeActionNames> | null {
    const factory = EditActionsStore.getActionByActionName(actionName);

    if (!factory) {
      return null;
    }

    const contributedEditAction = this.buildContributedEditAction(
      actionName,
      actionDefinition,
      factory,
    );

    const contributedEditActionWithUiOverrides = this.applyUIOverrides(
      actionName,
      contributedEditAction,
    );

    const displayName =
      contributedEditActionWithUiOverrides.displayName !== undefined
        ? contributedEditActionWithUiOverrides.displayName
        : actionDefinition.displayName || '';

    return {
      ...contributedEditActionWithUiOverrides,
      actionName,
      displayName,
    };
  }
}
